import React, { useState, useEffect, useRef } from 'react';
import { motion, useInView, animate } from 'framer-motion';
import './HomePage.css';

interface ServerStatus {
  online: boolean;
  players: {
    current: number;
    max: number;
  };
  serverName: string;
  connectUrl: string;
  lastUpdate: string;
  error?: string;
}

interface AnimatedNumberProps {
  to: number;
}

const AnimatedNumber: React.FC<AnimatedNumberProps> = ({ to }) => {
  const ref = useRef<HTMLSpanElement>(null);
  const isInView = useInView(ref, { once: true });

  useEffect(() => {
    if (isInView && ref.current) {
      animate(0, to, {
        duration: 2,
        onUpdate(value) {
          if (ref.current) {
            ref.current.textContent = value.toLocaleString('en-US', { maximumFractionDigits: 0 });
          }
        },
      });
    }
  }, [isInView, to]);

  return <span ref={ref}>0</span>;
};

const HomePage: React.FC = () => {
  const [serverStatus, setServerStatus] = useState<ServerStatus | null>(null);
  const [loading, setLoading] = useState(true);

  const [isGalleryHovered, setIsGalleryHovered] = useState(false);

  const fetchServerStatus = async () => {
    try {
      const apiBaseUrl = 'https://backend.cheese.place';
      const response = await fetch(`${apiBaseUrl}/api/server/status`);
      const data = await response.json();
      setServerStatus(data);
    } catch (error) {
      console.error('서버 상태 조회 실패:', error);
      setServerStatus({
        online: false,
        players: { current: 0, max: 999 },
        serverName: '치즈서버',
        connectUrl: 'https://cfx.re/join/xy83ye',
        lastUpdate: new Date().toISOString(),
        error: '연결 실패'
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchServerStatus();
    // 30초마다 상태 업데이트
    const interval = setInterval(fetchServerStatus, 30000);
    return () => clearInterval(interval);
  }, []);

  const galleryImages = [
    '/assets/swimming.png',
    '/assets/ghost.png',
    '/assets/hungry.png',
    '/assets/crying.png',
    '/assets/lestgo_pointing.png',
    '/assets/broken.png',
    '/assets/hurt.png',
  ];

  return (
    <div className="home-page">
      <div className="background-gradient">
        <div className="floating-shapes">
          <div className="shape shape-1"></div>
          <div className="shape shape-2"></div>
          <div className="shape shape-3"></div>
          <div className="bubble bubble-1"></div>
          <div className="bubble bubble-2"></div>
          <div className="bubble bubble-3"></div>
          <div className="bubble bubble-4"></div>
        </div>
      </div>

      <header className="header">
        <motion.nav 
          className="navbar glass-card"
          initial={{ y: -100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.8 }}
        >
          <div className="nav-brand">
            <img src="/assets/logo.png" alt="치즈 로고" className="logo" />
            <span className="brand-text">치즈서버</span>
          </div>
          <div className="nav-links">
            <a href="#home" className="nav-link">홈</a>
            <a href="#about" className="nav-link">소개</a>
            <a href="https://wiki.cheese.place" className="nav-link">위키</a>
          </div>
          <div className="nav-actions">
            <a href="https://cfx.re/join/xy83ye" className="btn-connect">서버 접속</a>
          </div>
        </motion.nav>
      </header>

      <main className="main-content">
        <section className="hero-section">
          <motion.div 
            className="hero-content"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.3 }}
          >
            <h1 className="hero-title">
              <span className="title-main">당신의 새 삶을 여기서.</span>
              <span className="title-highlight">치즈서버</span>
            </h1>
            <p className="hero-subtitle">
              부제목
            </p>
            <div className="hero-actions">
              <motion.a 
                href="#server-status"
                className="btn btn-primary glass-button"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <span>접속하기</span>
              </motion.a>
              <motion.a 
                href="#features"
                className="btn btn-secondary glass-button"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                더 알아보기
              </motion.a>
            </div>
          </motion.div>
          
          <motion.div 
            className="hero-visual"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 0.5 }}
          >
            <div className="floating-card glass-card">
              <img src="/assets/swimming.png" alt="여름 분위기" />
            </div>
          </motion.div>
        </section>

        <section id="features" className="features-section">
          <motion.div 
            className="section-header"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2>왜 치즈서버 인가요?</h2>
            <p>(설명1)</p>
          </motion.div>

          <div className="features-grid">
            <motion.div 
              className="feature-card glass-card"
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              whileHover={{ y: -10 }}
            >
              <div className="feature-icon">
                <img src="/assets/ghost.png" alt="설명2" />
              </div>
              <h3>(설명 2)</h3>
              <p>설명</p>
            </motion.div>

            <motion.div 
              className="feature-card glass-card"
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              whileHover={{ y: -10 }}
            >
              <div className="feature-icon">
                <img src="/assets/swimming.png" alt="설명3" />
              </div>
              <h3>(설명 3)</h3>
              <p>설명</p>
            </motion.div>

            <motion.div 
              className="feature-card glass-card"
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.3 }}
              whileHover={{ y: -10 }}
            >
              <div className="feature-icon">
                <img src="/assets/hungry.png" alt="설명4" />
              </div>
              <h3>(설명 4)</h3>
              <p>설명</p>
            </motion.div>
          </div>
        </section>

        <section id="gallery" className={`gallery-section ${isGalleryHovered ? 'hovered' : ''}`}>
          <motion.div 
            className="section-header"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2>갤러리</h2>
            <p>치즈서버의 다양한 순간들</p>
          </motion.div>
          <div className="gallery-wrapper" onMouseEnter={() => setIsGalleryHovered(true)} onMouseLeave={() => setIsGalleryHovered(false)}>
            <div className="gallery_track scroll-1">
              <div className="gallery-container">
                {[...galleryImages, ...galleryImages].map((src, index) => (
                  <motion.div
                    key={index}
                    className="gallery-item glass-card"
                    initial={{ opacity: 0, x: 50 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    whileHover={{ y: -10 }}
                  >
                    <img src={src} alt={`갤러리 이미지 ${index + 1}`} />
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
          <div className="gallery-wrapper" onMouseEnter={() => setIsGalleryHovered(true)} onMouseLeave={() => setIsGalleryHovered(false)}>
            <div className="gallery_track scroll-2">
              <div className="gallery-container">
                {[...galleryImages, ...galleryImages].map((src, index) => (
                  <motion.div
                    key={index}
                    className="gallery-item glass-card"
                    initial={{ opacity: 0, x: 50 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    whileHover={{ y: -10 }}
                  >
                    <img src={src} alt={`갤러리 이미지 ${index + 1}`} />
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section id="server-status" className="server-status glass-card">
          {loading ? (
            <h3>서버 상태 확인 중...</h3>
          ) : serverStatus ? (
            <>
              <h3>
                <span className="player-count">{serverStatus.players.current}명</span>
                이 치즈서버를 플레이하고 있어요
              </h3>
              <div className="status-grid">
                <div className="status-item">
                  <span className="status-label">서버 상태</span>
                  <span className={`status-value ${serverStatus.online ? 'online' : 'offline'}`}>
                    {serverStatus.online ? '온라인' : '오프라인'}
                  </span>
                </div>
                <div className="status-item">
                  <motion.a 
                    href={serverStatus.connectUrl}
                    className="btn btn-join glass-button"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    접속하기
                  </motion.a>
                </div>
              </div>
              {!serverStatus.online && (
                <div className="offline-message">
                  <p>Discord에 참여해보세요</p>
                  <motion.a 
                    href="https://discord.gg/cheese2024"
                    className="btn btn-discord glass-button"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    Discord로 이동
                  </motion.a>
                </div>
              )}
              {/*{serverStatus.error && (
                <p className="error-message" style={{ fontSize: '0.8em', opacity: 0.7, marginTop: '10px' }}>
                  {serverStatus.error}
                </p>
              )}*/}
            </>
          ) : (
            <h3>서버 상태를 불러올 수 없습니다</h3>
          )}
        </section>

        <section className="stats-section glass-card">
          <motion.div
            className="stats-content"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <p>
              <span className="stats-highlight">2024년 1월 1일</span>부터 지금까지{' '}
              <span className="stats-highlight"><AnimatedNumber to={123456} />명</span>이 플레이했어요
            </p>
          </motion.div>
        </section>
      </main>

      <footer className="footer glass-card">
        <div className="footer-content">
          <div className="footer-brand">
            <img src="/assets/logo.png" alt="치즈 로고" />
            <p>Footer 설명</p>
          </div>
          <div className="footer-links">
            <div className="link-group">
              <h4>바로가기</h4>
              <a href="https://cfx.re/join/xy83ye">서버 접속</a>
              <a href="https://wiki.cheese.place">서버 위키</a>
              <a href="/guide">시작 가이드</a>
            </div>
            <div className="link-group">
              <h4>커뮤니티</h4>
              <a href="https://discord.gg/cheese2024">디스코드</a>
            </div>
          </div>
        </div>
        <div className="footer-bottom">
          <p>&copy; 2025 치즈서버 | 데모</p>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
